/**
 * Deposit payment selector for the public quote page.
 *
 * When the tenant offers BOTH rails, this renders the same Card | Bank account
 * switch as the Payabli form, with the bank saving under its option, and one
 * Pay button whose amount and rail follow the switch.
 *
 * Rail availability is NOT the same question as surcharging. This used to key
 * the bank-transfer row off the surcharge preview, so a tenant that accepted
 * bank transfers but did not surcharge cards (the default) showed a single
 * card-labelled button and the customer had no way to pay by ACH at all.
 *
 * Direct ACH discounts reduce the required deposit after its final amount is
 * known. Older surcharge payloads keep their existing presentation.
 */
import { useState } from 'react';
import { BadgeDollarSign, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  CustomerPayButton,
  PaymentRailSwitch,
  SecureNote,
} from '@/components/organisms/payments/PaymentRailSwitch';
import { SurchargeDisclosure } from '@/components/organisms/payments/SurchargeDisclosure';
import {
  computePaymentMethodDiscount,
  effectivePaymentMethodDiscountPercent,
  formatDiscountPercent,
} from '@/lib/payments/paymentMethodDiscount';

export type DepositMethod = 'card' | 'ach';

export interface DepositSurchargeInfo {
  enabled: boolean;
  surchargePercent: number;
  achCheckDiscountEnabled?: boolean;
  achCheckDiscountPercent?: number;
  discountCents?: number;
  surchargeAmount: number;
  totalWithSurcharge: number;
  achNoFeeOffered: boolean;
  /**
   * The rails the customer is actually offered, server-resolved from the same
   * list the checkout-session path builds. Optional so payloads that predate
   * the fields keep their previous (surcharge-derived) behaviour instead of
   * collapsing to "no rails".
   */
  cardOffered?: boolean;
  achOffered?: boolean;
}

export interface DepositCTAProps {
  depositAmount: number;
  surcharge?: DepositSurchargeInfo;
  busy: boolean;
  pendingMethod: DepositMethod | null;
  onPay: (method?: DepositMethod) => void;
}

/**
 * Whether the customer is actually offered a choice of payment method.
 *
 * Exported because the deposit panel's heading depends on the same answer: it
 * used to say "Choose how you would like to pay" unconditionally, so a tenant
 * without card surcharging read an invitation to choose above a single button.
 * One predicate, so the heading and the buttons cannot drift apart.
 *
 * Driven by the server-resolved rails when the payload carries them: two rails
 * accepted ⇒ two CTAs, whether or not a card fee applies. `achNoFeeOffered` is
 * the legacy fallback for payloads that predate `cardOffered`/`achOffered`; it
 * is false whenever surcharging is off, which is exactly why it can no longer
 * be the primary signal.
 */
export function offersTwoDepositMethods(surcharge?: DepositSurchargeInfo): boolean {
  if (surcharge?.cardOffered !== undefined || surcharge?.achOffered !== undefined) {
    return Boolean(surcharge?.cardOffered && surcharge?.achOffered);
  }
  return Boolean(surcharge?.enabled && surcharge?.achNoFeeOffered);
}

/**
 * Bank transfer is the ONLY rail. The single-CTA branch below must then say so
 * — and pass the method explicitly — instead of rendering a card icon over a
 * checkout that can only take a bank account. Exported so a checkout opened
 * without this button (Approve and pay) passes the same method.
 */
export function isBankOnly(surcharge?: DepositSurchargeInfo): boolean {
  return surcharge?.achOffered === true && surcharge?.cardOffered === false;
}

function fmt(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function roundCurrency(amount: number): number {
  return Math.round(amount * 100) / 100;
}

export function DepositCTA({
  depositAmount,
  surcharge,
  busy,
  pendingMethod,
  onPay,
}: DepositCTAProps) {
  // Card first, as on the Payabli form. Chosen here, not in Checkout: the
  // session's amount depends on it (a card fee or a bank discount).
  const [rail, setRail] = useState<DepositMethod>('card');
  const twoOptions = offersTwoDepositMethods(surcharge);
  const directDiscount = surcharge?.achCheckDiscountEnabled === true;
  const bankQuote = computePaymentMethodDiscount(surcharge, Math.round(depositAmount * 100), 'ACH');
  const bankTotal = directDiscount ? bankQuote.chargeCents / 100 : depositAmount;

  if (twoOptions) {
    const surchargeAmount = surcharge?.surchargeAmount ?? 0;
    // A fee only exists when the tenant surcharges AND the computed fee is
    // non-zero. Without this the no-surcharge tenant's rows would read
    // "Includes $0.00 credit card fee" beside a "No fee" bank row — inventing
    // a difference between two identically-priced rails.
    const feeApplies = !directDiscount && surcharge?.enabled === true && surchargeAmount > 0;
    const savingsAmount = directDiscount ? bankQuote.discountCents / 100 : surchargeAmount;
    const savingsApply = directDiscount ? bankQuote.discountCents > 0 : feeApplies;
    // Prefer the server's figure — it is what Stripe will actually charge.
    // Only fall back to local arithmetic if the payload omitted it.
    const cardTotal = feeApplies
      ? (surcharge?.totalWithSurcharge ?? roundCurrency(depositAmount + surchargeAmount))
      : depositAmount;
    const discountPercent = directDiscount
      ? bankQuote.percent
      : effectivePaymentMethodDiscountPercent(
          depositAmount,
          surchargeAmount,
          surcharge?.surchargePercent,
        );
    return (
      <div className="flex w-full min-w-0 flex-col gap-4">
        <PaymentRailSwitch
          value={rail}
          onChange={setRail}
          disabled={busy}
          testIdPrefix="deposit-rail"
          achSavings={
            savingsApply ? (
              <span className="text-xs font-medium text-emerald-700">
                Save {fmt(savingsAmount)} ({formatDiscountPercent(discountPercent)})
              </span>
            ) : null
          }
        />
        <CustomerPayButton
          label={`Pay deposit ${fmt(rail === 'ach' ? bankTotal : cardTotal)}`}
          busy={busy && (pendingMethod === null || pendingMethod === rail)}
          disabled={busy}
          onClick={() => onPay(rail)}
          secureNote="Secure checkout by Stripe"
          testId={`deposit-pay-${rail}`}
        />
      </div>
    );
  }

  // Single method: the button carries no fee breakdown of its own, so the
  // standalone disclosure is the only place the surcharge is stated.
  //
  // A bank-only tenant gets the bank label, the bank icon and an EXPLICIT
  // 'ach' method — a card icon over a checkout that only accepts a bank
  // account reads as "no ACH here" to the one customer who has nothing else.
  const bankOnly = isBankOnly(surcharge);
  const singleMethodAmount = directDiscount
    ? bankOnly
      ? bankTotal
      : depositAmount
    : !bankOnly && surcharge?.enabled
      ? (surcharge.totalWithSurcharge ??
        roundCurrency(depositAmount + (surcharge.surchargeAmount ?? 0)))
      : depositAmount;
  return (
    <div className="flex w-full flex-col gap-3">
      <SurchargeDisclosure
        baseAmount={depositAmount}
        surchargeAmount={surcharge?.surchargeAmount ?? 0}
        enabled={!directDiscount && Boolean(surcharge?.enabled)}
        achOrCheckOffered={Boolean(surcharge?.achOffered ?? surcharge?.achNoFeeOffered)}
        className="rounded-2xl border-slate-200 bg-slate-50 px-4 py-3 text-slate-600"
      />
      {directDiscount && bankOnly && bankQuote.discountCents > 0 ? (
        <p className="text-center text-xs text-slate-600">
          Save {fmt(bankQuote.discountCents / 100)} ({formatDiscountPercent(bankQuote.percent)}) by
          bank transfer.
        </p>
      ) : null}
      <Button
        className="h-12 w-full rounded-2xl bg-black text-white shadow-[0_10px_24px_rgba(38,52,61,0.18)] transition hover:bg-black/90"
        onClick={() => onPay(bankOnly ? 'ach' : undefined)}
        disabled={busy}
        data-testid="deposit-pay-single"
      >
        {busy ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : bankOnly ? (
          <BadgeDollarSign className="mr-2 h-4 w-4" />
        ) : (
          <CreditCard className="mr-2 h-4 w-4" />
        )}
        {bankOnly ? 'Pay deposit by bank transfer' : 'Pay deposit'} {fmt(singleMethodAmount)}
      </Button>
      <SecureNote>Secure checkout by Stripe</SecureNote>
    </div>
  );
}
